import React, {
  useReducer,
  useContext,
  useEffect,
  createContext,
  useState,
  useMemo,
  useCallback,
} from "react"
import { useStaticQuery, graphql } from "gatsby"

import { Notification } from "../components/utils/Notification"
import { Locale } from "./Locale"

export const StoreContext = createContext()
export const FavoritesContext = createContext()
export const CurrencyContext = createContext()

const safeStorageGet = (storage, key) => {
  try {
    return storage?.getItem(key) ?? null
  } catch (error) {
    return null
  }
}

const safeStorageSet = (storage, key, value) => {
  try {
    storage?.setItem(key, value)
  } catch (error) {
    // Safari can reject storage access in private/restricted contexts.
  }
}

const getPropertyKey = property =>
  property?.reference || property?.originalId || property?.id || property?.slug

const sanitizeProperties = properties =>
  Array.isArray(properties)
    ? properties.filter(property => getPropertyKey(property))
    : []

const storeReducer = (state, action) => {
  switch (action.type) {
    case "toggle": {
      const propertyKey = getPropertyKey(action.property)

      if (!propertyKey) {
        return state
      }

      const properties = sanitizeProperties(state.properties)
      const index = properties.findIndex(
        property => getPropertyKey(property) === propertyKey
      )

      if (index === -1) {
        return {
          ...state,
          properties: properties.concat(action.property),
          action: "added",
        }
      }

      return {
        ...state,
        properties: properties.filter(
          property => getPropertyKey(property) !== propertyKey
        ),
        action: "removed",
      }
    }
    case "reset": {
      // Remove all properties
      return { ...state, properties: [], action: "reset" }
    }
    case "change_currency": {
      // Change language
      return { ...state, currency: action.currency, action: "change_currency" }
    }
    default: {
      throw new Error(`Unhandled action type ${action.type}`)
    }
  }
}

const StoreProvider = ({ children }) => {
  const { locale: contextLocale } = useContext(Locale)
  // StoreProvider est monté au-dessus de LocaleProvider (wrapRootElement) :
  // le contexte vaut toujours 'en' ici. La langue des notifications suit
  // donc l'URL courante, relue à chaque action (favori ajouté/retiré).
  const [pathLocale, setPathLocale] = useState(null)
  const locale = pathLocale || contextLocale || "en"

  const [showAdded, setShowAdded] = useState(false)
  const [showRemoved, setShowRemoved] = useState(false)
  const [showReset, setShowReset] = useState(false)

  const store = useStaticQuery(graphql`
    query StoreQuery {
      en: datoCmsSidebar(locale: { eq: "en" }) {
        ...Sidebar
      }
      fr: datoCmsSidebar(locale: { eq: "fr" }) {
        ...Sidebar
      }
      currencies: allDatoCmsCurrency {
        all: distinct(field: code)
      }
    }
  `)

  let localStore = () => {
    if (typeof window !== `undefined`) {
      let currency = "EUR"
      const localCurrency = safeStorageGet(window.localStorage, "currency")

      if (store.currencies.all.includes(localCurrency)) {
        currency = localCurrency
      }

      let properties = []
      try {
        properties = JSON.parse(
          safeStorageGet(window.localStorage, "properties") || "[]"
        )
      } catch (error) {
        console.error(error)
      }

      return { currency, properties: sanitizeProperties(properties) }
    }

    return { currency: "EUR", properties: [] }
  }

  const [state, dispatch] = useReducer(
    storeReducer,
    { currency: "EUR", properties: [] },
    localStore
  )

  const toggleProperty = useCallback(
    property => dispatch({ type: "toggle", property }),
    []
  )
  const resetFavorites = useCallback(() => dispatch({ type: "reset" }), [])
  const changeCurrency = useCallback(
    currency => dispatch({ type: "change_currency", currency }),
    []
  )

  const storeValue = useMemo(() => ({ state, dispatch }), [state])
  const favoritesValue = useMemo(
    () => ({
      properties: state.properties || [],
      toggleProperty,
      resetFavorites,
    }),
    [state.properties, toggleProperty, resetFavorites]
  )
  const currencyValue = useMemo(
    () => ({
      currency: state.currency || "EUR",
      changeCurrency,
    }),
    [state.currency, changeCurrency]
  )

  useEffect(() => {
    if (typeof window !== "undefined") {
      safeStorageSet(
        window.localStorage,
        "properties",
        JSON.stringify(sanitizeProperties(state.properties))
      )
      safeStorageSet(
        window.localStorage,
        "currency",
        state.currency ? state.currency : "EUR"
      )
    }

    // Display notification
    if (typeof window !== "undefined") {
      const seg = window.location.pathname.split("/")[1]
      setPathLocale(seg === "fr" || seg === "en" ? seg : null)
    }
    setShowAdded(false)
    setShowReset(false)
    setShowRemoved(false)

    if (state.action === "added") {
      setShowAdded(true)
    } else if (state.action === "removed") {
      setShowRemoved(true)
    } else if (state.action === "reset") {
      setShowReset(true)
    }
  }, [state])

  return (
    <StoreContext.Provider value={storeValue}>
      <CurrencyContext.Provider value={currencyValue}>
        <FavoritesContext.Provider value={favoritesValue}>
          {children}
          <Notification
            title={store[locale].notificationSavedTitle}
            subtitle={store[locale].notificationSavedSubtitle}
            onClose={() => setShowAdded(false)}
            show={showAdded}
          />
          <Notification
            title={store[locale].notificationRemovedTitle}
            subtitle={store[locale].notificationRemovedSubtitle}
            onClose={() => setShowRemoved(false)}
            show={showRemoved}
          />
          <Notification
            title={store[locale].notificationResetTitle}
            subtitle={store[locale].notificationResetSubtitle}
            onClose={() => setShowReset(false)}
            show={showReset}
          />
        </FavoritesContext.Provider>
      </CurrencyContext.Provider>
    </StoreContext.Provider>
  )
}

const useStore = () => useContext(StoreContext)
const useFavorites = () => useContext(FavoritesContext)
const useCurrency = () => useContext(CurrencyContext)

export { StoreProvider, useStore, useFavorites, useCurrency }
